import { centsToFormattedDollars } from '@fruitful/fruitils';
import React, {
    type Dispatch,
    type SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { type LayoutChangeEvent, Pressable, View } from 'react-native';
import Animated, {
    type SharedValue,
    useAnimatedProps,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
    withSpring,
} from 'react-native-reanimated';
import Svg, {
    Defs,
    LinearGradient,
    Path,
    Rect,
    Stop,
    Text as SvgText,
} from 'react-native-svg';
import {
    FontFamilyName,
    FruitfulLogo,
    isWeb,
    redactPrivate,
    tokens,
    useThemeColors,
} from 'smoothie';

import { MoneyMapFilterState } from '../types/MoneyMapFilterState';

interface BarItem {
    label: string;
    color?: string;
    value: SharedValue<number>;
    filter: MoneyMapFilterState;
}

interface BarProps {
    barItem: BarItem;
    index: number;
}

const createAnimatedComponent = Animated.createAnimatedComponent;

const AnimatedSvg = createAnimatedComponent(Svg);
const AnimatedRect = createAnimatedComponent(Rect);
const AnimatedText = createAnimatedComponent(SvgText);
const AnimatedPath = createAnimatedComponent(Path);

interface MoneyMapVisualProps {
    incomeTotalCents: number;
    billsTotalCents?: number | null;
    spendTotalCents?: number | null;
    goalsTotalCents?: number | null;
    filter: MoneyMapFilterState;
    setFilter: Dispatch<SetStateAction<MoneyMapFilterState>>;
    isIncomeSplitEligible: boolean;
    visualSizing?: {
        maxExpandedBarHeight?: number;
        maxCollapsedBarHeight?: number;
    };
}

export const MoneyMapVisual = ({
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    filter,
    setFilter,
    isIncomeSplitEligible,
    visualSizing,
}: MoneyMapVisualProps) => {
    const { getColor } = useThemeColors();
    const isAllFilterSelected = filter === MoneyMapFilterState.ALL;
    const isAllFilterSelectedShared = useSharedValue(isAllFilterSelected);
    const [isInitialVisualLayout, setIsInitialVisualLayout] = useState(true);

    // General spacing, font, animation configs
    const gap = 4;
    const gapBetweenIncomeConnectorAndBars = 86;

    const labelFontSize = 12;
    const valueFontSize = 16;

    const maxVisualWidth = 700;
    const [visualWidth, setVisualWidth] = useState(0);

    const activeColorMap: Record<string, string> = {
        [MoneyMapFilterState.BILLS as string]: getColor('$dropletBlue500'),
        [MoneyMapFilterState.SPEND as string]: getColor('$wasabi500'),
        [MoneyMapFilterState.GOALS as string]: getColor('$peach500'),
    };

    const inactiveColorMap: Record<string, string> = {
        [MoneyMapFilterState.BILLS as string]: getColor('$dropletBlue200'),
        [MoneyMapFilterState.SPEND as string]: getColor('$wasabi200'),
        [MoneyMapFilterState.GOALS as string]: getColor('$peach200'),
    };

    // Bills / Spend / Goals - Bars config

    const getBarColor = (barFilter: MoneyMapFilterState) => {
        return filter === barFilter || isAllFilterSelected
            ? activeColorMap[barFilter]
            : inactiveColorMap[barFilter];
    };

    const barDisplayValues: Record<MoneyMapFilterState, number> = {
        [MoneyMapFilterState.ALL]: 0,
        [MoneyMapFilterState.BILLS]: billsTotalCents ?? 0,
        [MoneyMapFilterState.SPEND]: spendTotalCents ?? 0,
        [MoneyMapFilterState.GOALS]: goalsTotalCents ?? 0,
    };

    const animatedBillsTotalCents = useDerivedValue(() => {
        return withSpring(billsTotalCents ?? 0);
    });
    const animatedSpendTotalCents = useDerivedValue(() => {
        return withSpring(spendTotalCents ?? 0);
    });
    const animatedGoalsTotalCents = useDerivedValue(() => {
        return withSpring(goalsTotalCents ?? 0);
    });

    const bars = [
        {
            label: 'Bills',
            color: getBarColor(MoneyMapFilterState.BILLS),
            value: animatedBillsTotalCents,
            filter: MoneyMapFilterState.BILLS,
        },
        {
            label: 'Spend',
            color: getBarColor(MoneyMapFilterState.SPEND),
            value: animatedSpendTotalCents,
            filter: MoneyMapFilterState.SPEND,
        },
        {
            label: 'Goals',
            color: getBarColor(MoneyMapFilterState.GOALS),
            value: animatedGoalsTotalCents,
            filter: MoneyMapFilterState.GOALS,
        },
    ].filter((bar) => barDisplayValues[bar.filter] > 0);

    const barWidth = useMemo(() => {
        if (visualWidth === 0) {
            return 0;
        }

        return (visualWidth - gap * (bars.length - 1)) / bars.length;
    }, [visualWidth, bars.length]);

    const minBarHeight = 60;
    const maxExpandedBarHeight = visualSizing?.maxExpandedBarHeight ?? 384; // Maximum height of the bar for expanded state.
    const maxCollapsedBarHeight = visualSizing?.maxCollapsedBarHeight ?? 240; // Maximum height of the bar for collapsed state.
    const maxBarHeight = useDerivedValue(() => {
        return withSpring(
            isAllFilterSelectedShared.value
                ? maxExpandedBarHeight
                : maxCollapsedBarHeight,
        );
    });
    const maxAmount = useDerivedValue(() => {
        return Math.max(
            animatedBillsTotalCents.value,
            animatedSpendTotalCents.value,
            animatedGoalsTotalCents.value,
            1,
        );
    });

    const BarPressable = ({ barItem, index }: BarProps) => {
        const barX = index * (barWidth + gap);
        const barHeight = useDerivedValue(() => {
            const desiredHeight =
                (barItem.value.value / maxAmount.value) * maxBarHeight.value;

            return Math.max(desiredHeight, minBarHeight);
        });

        const barY = useDerivedValue(() => {
            return (
                curveYPosition.value +
                gapBetweenIncomeConnectorAndBars +
                (maxBarHeight.value - barHeight.value)
            );
        });
        const animatedBarPressableStyle = useAnimatedStyle(() => {
            return {
                top: barY.value,
                height: barHeight.value,
            };
        });

        return (
            <Animated.View
                style={[
                    {
                        position: 'absolute',
                        left: barX,
                        width: barWidth,
                    },
                    animatedBarPressableStyle,
                ]}
            >
                <Pressable
                    onPress={() => {
                        if (filter === barItem.filter) {
                            setFilter(MoneyMapFilterState.ALL);
                        } else {
                            setFilter(barItem.filter);
                        }

                        setIsInitialVisualLayout(false);
                    }}
                    style={{
                        flex: 1,
                        backgroundColor: 'transparent',
                    }}
                />
            </Animated.View>
        );
    };

    const BarCurve = ({ barItem, index }: BarProps) => {
        const startX = visualWidth / 2;
        const startY = curveYPosition;

        // Get actual index from the bars array for positioning.
        const realIndex = bars.findIndex((b) => b.label === barItem.label);

        const barHeight = useDerivedValue(() => {
            const desiredHeight =
                (barItem.value.value / maxAmount.value) * maxBarHeight.value;

            return Math.max(desiredHeight, minBarHeight);
        });
        const barX = realIndex * (barWidth + gap);
        const barTopY = useDerivedValue(() => {
            return (
                curveYPosition.value +
                gapBetweenIncomeConnectorAndBars +
                (maxBarHeight.value - barHeight.value)
            );
        });

        const endX = barX + barWidth / 2;
        const endY = useDerivedValue(() => {
            return barTopY.value - gap;
        });

        // Compute the control points for the cubic bezier curve, using a standard linear interpolation pattern.
        // Adjust min/max multipliers as needed to achieve desired design.
        const c1y = useDerivedValue(() => {
            const curveHeight = Math.abs(endY.value - startY.value);
            const minMultiplier = -0.5;
            const maxMultiplier = 1.5;
            const ratio = curveHeight / maxCurveHeight.value;
            const multiplier =
                maxMultiplier - ratio * (maxMultiplier - minMultiplier);
            return startY.value + curveHeight * multiplier;
        });
        const c2y = useDerivedValue(() => {
            const curveHeight = Math.abs(endY.value - startY.value);
            const minMultiplier = 0.3;
            const maxMultiplier = 0.4;
            const ratio = curveHeight / maxCurveHeight.value;
            const multiplier =
                maxMultiplier - ratio * (maxMultiplier - minMultiplier);
            return startY.value + curveHeight * multiplier;
        });
        const animatedCurveProps = useAnimatedProps(() => {
            const d = `M${startX},${startY.value} C${startX},${c1y.value} ${endX},${c2y.value} ${endX},${endY.value}`;
            return { d };
        });

        return (
            <AnimatedPath
                key={`curve-${barItem.label}-${index}`} // Key needs to include all of these to handle the shifting ordering of curves.
                stroke={barItem.color}
                strokeWidth={12}
                fill="none"
                animatedProps={animatedCurveProps}
            />
        );
    };

    const Bar = ({ barItem, index }: BarProps) => {
        const barX = index * (barWidth + gap);
        const barHeight = useDerivedValue(() => {
            const desiredHeight =
                (barItem.value.value / maxAmount.value) * maxBarHeight.value;

            return Math.max(desiredHeight, minBarHeight);
        });
        const barY = useDerivedValue(() => {
            return (
                curveYPosition.value +
                gapBetweenIncomeConnectorAndBars +
                (maxBarHeight.value - barHeight.value)
            );
        });

        const animatedBarProps = useAnimatedProps(() => {
            return {
                y: barY.value,
                height: barHeight.value,
            };
        });
        const animatedBarLabelProps = useAnimatedProps(() => {
            return {
                y: totalHeight.value - 12, // Manual offset per designs.
            };
        });
        const animatedBarValueProps = useAnimatedProps(() => {
            return {
                y: barY.value + 23, // Manual offset per designs.
            };
        });

        return (
            <React.Fragment>
                <AnimatedRect
                    x={barX}
                    width={barWidth}
                    rx={8}
                    fill={barItem.color}
                    animatedProps={animatedBarProps}
                />
                <AnimatedText
                    x={barX + barWidth / 2}
                    fill="#000"
                    fontFamily={FontFamilyName.FigtreeMedium}
                    fontSize={valueFontSize}
                    fontWeight="700"
                    textAnchor="middle"
                    animatedProps={animatedBarValueProps}
                    {...redactPrivate()}
                >
                    {centsToFormattedDollars(
                        barDisplayValues[barItem.filter] ?? 0,
                        false,
                    )}
                </AnimatedText>
                <AnimatedText
                    x={barX + barWidth / 2}
                    fill="#000000"
                    fontFamily={FontFamilyName.FigtreeMedium}
                    fontSize={labelFontSize}
                    fontWeight="500"
                    textAnchor="middle"
                    animatedProps={animatedBarLabelProps}
                >
                    {barItem.label}
                </AnimatedText>
            </React.Fragment>
        );
    };

    // Income config
    const incomeColor =
        filter === MoneyMapFilterState.ALL
            ? tokens.color.light.green.green500
            : tokens.color.light.green.green200;
    const incomeYPosition = 0;
    const incomeTextOffset = 8;
    const collapsedHorizontalIncomeTextOffset = 16;
    const incomeWidth = 225;
    const expandedIncomeHeight = 80;
    const collapsedIncomeHeight = 40;
    const incomeHeight = useDerivedValue(() => {
        const height = isAllFilterSelectedShared.value
            ? expandedIncomeHeight
            : collapsedIncomeHeight;

        return withSpring(height);
    });
    const animatedIncomeProps = useAnimatedProps(() => {
        return {
            height: incomeHeight.value,
        };
    });
    const animatedIncomePressableStyle = useAnimatedStyle(() => {
        return {
            top: incomeYPosition,
            height: incomeHeight.value,
        };
    });

    // Income text config
    const expandedIncomeLabelYPosition =
        incomeYPosition + incomeTextOffset + labelFontSize / 3 + 8; // Font Size / 3 is approximation for vertically centering based on content height. Adding extra padding of 8 to match designs.
    const collapsedIncomeLabelYPosition = useDerivedValue(() => {
        return incomeYPosition + incomeHeight.value / 2 + labelFontSize / 3;
    });
    const incomeLabelYPosition = useDerivedValue(() => {
        return isAllFilterSelectedShared.value
            ? expandedIncomeLabelYPosition
            : collapsedIncomeLabelYPosition.value;
    });
    const expandedIncomeLabelXPosition = visualWidth / 2;
    const collapsedIncomeLabelXPosition =
        visualWidth / 2 - incomeWidth / 2 + collapsedHorizontalIncomeTextOffset;
    const incomeLabelXPosition = useDerivedValue(() => {
        const x = isAllFilterSelectedShared.value
            ? expandedIncomeLabelXPosition
            : collapsedIncomeLabelXPosition;
        return isInitialVisualLayout ? x : withSpring(x);
    });
    const animatedIncomeLabelProps = useAnimatedProps(() => {
        return {
            x: incomeLabelXPosition.value,
            y: incomeLabelYPosition.value,
        };
    });

    const expandedIncomeValueYPosition = useDerivedValue(() => {
        return (
            incomeYPosition +
            incomeHeight.value -
            incomeTextOffset -
            valueFontSize / 3
        );
    });
    const collapsedIncomeValueYPosition = useDerivedValue(() => {
        return incomeYPosition + incomeHeight.value / 2 + valueFontSize / 3;
    });
    const incomeValueYPosition = useDerivedValue(() => {
        return isAllFilterSelectedShared.value
            ? expandedIncomeValueYPosition.value
            : collapsedIncomeValueYPosition.value;
    });
    const expandedIncomeValueXPosition = visualWidth / 2;
    const collapsedIncomeValueXPosition =
        visualWidth / 2 + incomeWidth / 2 - collapsedHorizontalIncomeTextOffset;
    const incomeValueXPosition = useDerivedValue(() => {
        const x = isAllFilterSelectedShared.value
            ? expandedIncomeValueXPosition
            : collapsedIncomeValueXPosition;
        return isInitialVisualLayout ? x : withSpring(x);
    });
    const animatedIncomeValueProps = useAnimatedProps(() => {
        return {
            x: incomeValueXPosition.value,
            y: incomeValueYPosition.value,
        };
    });

    // Income Connector config
    const incomeConnectorWidth = 12;
    const expandedIncomeConnectorHeight = 54;
    const collapsedIncomeConnectorHeight = 36;
    const incomeConnectorHeight = useDerivedValue(() => {
        const height = isAllFilterSelectedShared.value
            ? expandedIncomeConnectorHeight
            : collapsedIncomeConnectorHeight;

        return withSpring(height);
    });
    const incomeConnectorYPosition = useDerivedValue(() => {
        return incomeHeight.value + gap;
    });
    const animatedIncomeConnectorProps = useAnimatedProps(() => {
        return {
            y: incomeConnectorYPosition.value,
            height: incomeConnectorHeight.value,
        };
    });

    // Logo config
    const logoWidth = 48;
    const logoHeight = 48;
    const logoBorderWidth = 4;
    const logoYPosition = useDerivedValue(() => {
        return (
            incomeConnectorYPosition.value +
            incomeConnectorHeight.value -
            logoHeight / 2 -
            logoBorderWidth
        );
    });
    const innerLogoWidth = logoWidth / 2;
    const innerLogoHeight = logoHeight / 2;
    const animatedLogoStyle = useAnimatedStyle(() => {
        return {
            top: logoYPosition.value,
        };
    });

    // Curves
    const curveYPosition = useDerivedValue(() => {
        return incomeConnectorYPosition.value + incomeConnectorHeight.value; // No gap between income connector end and curve start.
    });
    const maxCurveHeight = useDerivedValue(() => {
        return (
            curveYPosition.value +
            gapBetweenIncomeConnectorAndBars +
            maxBarHeight.value
        );
    });

    // Calculate svg height
    const totalHeight = useDerivedValue(() => {
        return (
            curveYPosition.value +
            gapBetweenIncomeConnectorAndBars +
            maxBarHeight.value
        );
    });
    const animatedSvgProps = useAnimatedProps(() => {
        return {
            height: totalHeight.value,
        };
    });

    // Handle layout changes on container resizing.
    const handleContainerLayout = (event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setVisualWidth(Math.min(width, maxVisualWidth)); // Visual will either expand to fill parent container, which is reactive to window resizing, or it is a static max width.
    };

    // Animate layout changes based on filter change.
    useEffect(() => {
        isAllFilterSelectedShared.value = isAllFilterSelected;
    }, [isAllFilterSelected]);

    return (
        <View
            style={{
                width: '100%',
                alignItems: 'center',
            }}
            onLayout={handleContainerLayout}
        >
            {/* Register custom font for web platform. */}
            {isWeb && (
                <style>
                    {`
                        @font-face {
                            font-family: '${FontFamilyName.FigtreeMedium}';
                            src: url('https://d1pwidzl9kib4u.cloudfront.net/fonts/figtree/Figtree-Medium.ttf') format("truetype");
                            font-weight: 500;
                            font-style: normal;
                        }
                    `}
                </style>
            )}
            <View style={{ width: visualWidth }}>
                {/* Render Pressable overlays that render above the underlying SVG elements, in order to achieve click/press functionality.
                It is important that these have the same shape and size of the underlying svg element. */}
                <View
                    style={{
                        zIndex: 1,
                    }}
                >
                    <Animated.View
                        style={[
                            {
                                position: 'absolute',
                                left: (visualWidth - incomeWidth) / 2,
                                width: incomeWidth,
                            },
                            animatedIncomePressableStyle,
                        ]}
                    >
                        {/* Income Pressable */}
                        <Pressable
                            onPress={() => {
                                setFilter(MoneyMapFilterState.ALL);
                                setIsInitialVisualLayout(false);
                            }}
                            style={{
                                flex: 1,
                                backgroundColor: 'transparent',
                            }}
                        />
                    </Animated.View>

                    {/* Bills / Spend / Goals Bar Pressables */}
                    {bars.map((bar, index) => (
                        <BarPressable
                            key={`${bar.label}-pressable`}
                            barItem={bar}
                            index={index}
                        />
                    ))}
                </View>

                {/* Render the Fruitful Logo */}
                {/* Ideally, the Fruitful logo would be rendered as an Svg inside of the Svg below; however, specifically on iOS, animating the position of this Svg has not been working with either position x,y properties or transforms. Web does animate as expected. */}
                {isIncomeSplitEligible && visualWidth > 0 && (
                    <Animated.View
                        style={[
                            {
                                position: 'absolute',
                                left:
                                    visualWidth / 2 -
                                    logoWidth / 2 -
                                    logoBorderWidth,
                                padding: 12,
                                borderRadius: 20,
                                backgroundColor: getColor('$gray1100'),
                                borderWidth: logoBorderWidth,
                                borderColor: getColor('$white'),
                                zIndex: 1,
                            },
                            animatedLogoStyle,
                        ]}
                    >
                        <FruitfulLogo
                            color={getColor('$white')}
                            width={innerLogoWidth}
                            height={innerLogoHeight}
                        />
                    </Animated.View>
                )}

                {/* Render the SVG Elements */}
                <AnimatedSvg
                    width={visualWidth}
                    animatedProps={animatedSvgProps}
                >
                    {/* Define the linear gradients to be used by the income connector */}
                    {/* @ts-expect-error - TODO - Not sure why Typescript is unhappy with Defs usage */}
                    <Defs>
                        <LinearGradient
                            key="grad-income-connector"
                            id="grad-income-connector"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <Stop offset="0%" stopColor={incomeColor} />
                            <Stop
                                offset="100%"
                                stopColor={
                                    bars.find((bar) => bar.filter === filter)
                                        ?.color ??
                                    getBarColor(MoneyMapFilterState.BILLS) // Bills is the default curve rendered on top, so blend into that.
                                }
                            />
                        </LinearGradient>
                    </Defs>

                    {/* Income */}
                    <AnimatedRect
                        x={(visualWidth - incomeWidth) / 2}
                        y={incomeYPosition}
                        width={incomeWidth}
                        rx={8}
                        fill={incomeColor}
                        animatedProps={animatedIncomeProps}
                    />
                    <AnimatedText
                        fill="#000000"
                        fontFamily={FontFamilyName.FigtreeMedium}
                        fontSize={labelFontSize}
                        fontWeight="500"
                        textAnchor={isAllFilterSelected ? 'middle' : 'start'} // Text anchor is not animatable.
                        animatedProps={animatedIncomeLabelProps}
                    >
                        Monthly income
                    </AnimatedText>
                    <AnimatedText
                        fill="#000000"
                        fontFamily={FontFamilyName.FigtreeMedium}
                        fontSize={valueFontSize}
                        fontWeight="bold"
                        textAnchor={isAllFilterSelected ? 'middle' : 'end'} // Text anchor is not animatable.
                        animatedProps={animatedIncomeValueProps}
                        {...redactPrivate()}
                    >
                        {centsToFormattedDollars(incomeTotalCents, false)}
                    </AnimatedText>

                    {/* Income Connector */}
                    <AnimatedRect
                        x={visualWidth / 2 - incomeConnectorWidth / 2}
                        width={incomeConnectorWidth}
                        fill="url(#grad-income-connector)"
                        animatedProps={animatedIncomeConnectorProps}
                    />

                    {/* Curves */}

                    {/* If a filter is selected for a corresponding bar / curve, render that curve last so that it renders on top of the stack of curves. */}
                    {/* Note: Shifting the order of the curves may cause rendering issues, so we only do it when the curve overlap is needed, which is only on money maps without income split. */}
                    {(isIncomeSplitEligible
                        ? bars
                        : [
                              ...bars
                                  .slice()
                                  .reverse() // Reversing array so that Bills is rendered last as the default top of the stack when curves are overlapped.
                                  .filter((bar) => bar.filter !== filter),
                              ...bars.filter((bar) => bar.filter === filter),
                          ]
                    ).map((bar, index) => (
                        <BarCurve
                            key={`${bar.label}-curve`}
                            barItem={bar}
                            index={index}
                        />
                    ))}

                    {/* Bills / Spend / Goal - Bars */}
                    {bars.map((bar, index) => (
                        <Bar key={bar.label} barItem={bar} index={index} />
                    ))}
                </AnimatedSvg>
            </View>
        </View>
    );
};
